import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Tabs, Card, Input, message, Select, Button } from "antd";
import { GiveawayModal } from "./giveaway/GiveawayModal";
import { formContent } from "../../utils/formContent";
import useFeedbackFormService from "../../services/feedbackFormService";
import useCloudinaryService from "../../services/cloudinaryService";
import useContestService from "../../services/contestService";
import StarRating from "../Dashboard/Pages/Feedback/StarRating";
import GridBackground from "./templates/GridBackground";
import BubbleBackground from "./templates/BubbleBackground";
import DiagonalLinesBackground from "./templates/DiagonalBackground";
import SubtleBackground from "./templates/SubtleBackground";
import DotMatrixBackground from "./templates/DotMatrixBackground";
import WaveBackground from "./templates/WaveBackground";
import useFeedbackService from "../../services/feedbackService";
import PositiveSubmit from "./submitSites/PositiveSubmit";
import NegativeSubmit from "./submitSites/NegativeSubmit";
import { motion, AnimatePresence } from "framer-motion";
import ImageCapture from "./ImageCapture";

const designs = [
  DotMatrixBackground,
  BubbleBackground,
  DiagonalLinesBackground,
  WaveBackground,
  GridBackground,
  SubtleBackground,
];

const FeedbackFormPage = () => {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  // ✅ Giveaway Modal States
  const [showGiveawayModal, setShowGiveawayModal] = useState(false);
  const [hasSeenGiveaway, setHasSeenGiveaway] = useState(false);
  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [color, setColor] = useState("");
  const [responses, setResponses] = useState({});
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [triggerSuccess, setTriggerSuccess] = useState(false);
  const [triggerFailure, setTriggerFailure] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const navigate = useNavigate();
  const { saveFeedback } = useFeedbackService();
  const { getFeedbackFormById } = useFeedbackFormService();
  const { uploadImageDirectly } = useCloudinaryService();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const viewMode = params.get("viewmode");
  const [aspectClass, setAspectClass] = useState("aspect-[4/3]");
  const { getContestById } = useContestService();
  const [captureImages, setCapturedImage] = useState([]);
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  const lang = form?.language || "DE";
  const t = formContent[lang];
  useEffect(() => {
    if (viewMode === "readonly") {
      setDisableButton(true);
    }
  }, [viewMode]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [anonym, setAnonym] = useState(false);
  useEffect(() => {
    const fetchForm = async () => {
      try {
        const fetchedForm = await getFeedbackFormById(id);
        fetchedForm.questions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setForm(fetchedForm);
        setColor(fetchedForm.designColor);

        if (fetchedForm.hasContest && fetchedForm.contest) {
          const mockContest = await getContestById(fetchedForm.contest);
          console.log(mockContest);
          setContest(mockContest);
          if (mockContest.status === "active") {
            setTimeout(() => {
              setShowGiveawayModal(true);
              setHasSeenGiveaway(true);
            }, 2000);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [id]);

  const handleAnswerChange = (questionId, questionText, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        question: questionText,
        answer: value,
      },
    }));
  };

  const handleYesNoChange = (questionId, questionText, field, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        question: questionText,
        // prev[...] könnte schon choice oder comment haben
        ...prev[questionId],
        [field]: value,
      },
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="spinner-border animate-spin w-10 h-10 border-4 rounded-full border-orange-500"></div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="text-center mt-10 text-gray-600 text-lg">
        {t.feedbackNotFound}
      </div>
    );
  }

  if (form.status !== "active") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-2">
            {t.formInactiveTitle || "Formular derzeit nicht verfügbar"}
          </h2>
          <p className="text-gray-600">
            {t.formInactiveDescription ||
              "Dieses Feedback-Formular wurde deaktiviert und kann aktuell nicht ausgefüllt werden."}
          </p>
        </div>
      </div>
    );
  }

  const renderQuestion = (question, index) => {
    switch (question.type) {
      case "multiple-choice":
        return (
          <div key={index} className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-2">
              {question.questionText}
            </label>
            <div className="space-y-2">
              {question.options.map((option, idx) => (
                <div key={idx} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`option-${index}-${idx}`}
                    className="h-5 w-5 text-orange-500 border-gray-300 rounded focus:ring-orange-400"
                    checked={
                      responses[question._id]?.answer?.includes(option) || false
                    }
                    onChange={(e) => {
                      const currentAnswers =
                        responses[question._id]?.answer || [];
                      const updatedAnswers = e.target.checked
                        ? [...currentAnswers, option]
                        : currentAnswers.filter((ans) => ans !== option);
                      handleAnswerChange(
                        question._id,
                        question.questionText,
                        updatedAnswers
                      );
                    }}
                  />
                  <label
                    htmlFor={`option-${index}-${idx}`}
                    className="ml-3 text-gray-800"
                  >
                    {option}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );
      case "single-choice":
        return (
          <div key={index} className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-2">
              {question.questionText}
            </label>
            <div className="space-y-2">
              {question.options.map((option, idx) => (
                <div key={idx} className="flex items-center">
                  <input
                    type="radio"
                    id={`option-${index}-${idx}`}
                    name={`question-${index}`}
                    checked={responses[question._id]?.answer === option}
                    className="h-5 w-5 text-orange-500 border-gray-300 rounded focus:ring-orange-400"
                    onChange={() =>
                      handleAnswerChange(
                        question._id,
                        question.questionText,
                        option
                      )
                    }
                  />
                  <label
                    htmlFor={`option-${index}-${idx}`}
                    className="ml-3 text-gray-800"
                  >
                    {option}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );
      case "text":
        return (
          <div key={index} className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-2">
              {question.questionText}
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-lg shadow-sm focus:ring-orange-400 focus:border-orange-400 p-3 text-gray-700"
              rows="4"
              placeholder="Geben Sie hier Ihre Antwort ein..."
              value={responses[question._id]?.answer || ""}
              onChange={(e) =>
                handleAnswerChange(
                  question._id,
                  question.questionText,
                  e.target.value
                )
              }
            ></textarea>
          </div>
        );
      case "yesno":
        const resp = responses[question._id] || {};
        return (
          <div key={index} className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-2">
              {question.questionText}
            </label>
            {/* Ja/Nein Auswahl */}
            <Select
              placeholder={t.chooseYesNoPlaceholder}
              value={resp.choice}
              onChange={(val) =>
                handleYesNoChange(
                  question._id,
                  question.questionText,
                  "choice",
                  val
                )
              }
              style={{ width: 240 }}
            >
              <Select.Option value="Ja">Ja</Select.Option>
              <Select.Option value="Nein">Nein</Select.Option>
            </Select>
            {/* Freitext nur, wenn choice gesetzt */}
            {resp.choice && (
              <textarea
                className="w-full border border-gray-300 rounded-lg shadow-sm focus:ring-orange-400 focus:border-orange-400 p-3 text-gray-700 mt-3"
                rows={3}
                placeholder={` ${
                  lang === "DE" ? "Warum" : "Why"
                } ${resp.choice.toLowerCase()}?`}
                value={resp.comment || ""}
                onChange={(e) =>
                  handleYesNoChange(
                    question._id,
                    question.questionText,
                    "comment",
                    e.target.value
                  )
                }
              />
            )}
          </div>
        );
      case "star-rating":
        return (
          <div key={index} className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-2">
              {question.questionText}
            </label>
            <StarRating
              stars={question.stars}
              onSelect={(value) =>
                handleAnswerChange(question._id, question.questionText, value)
              }
            />
          </div>
        );
      default:
        return null;
    }
  };

  // Funktion zur Umwandlung von "Design 1" -> Index 0, "Design 2" -> Index 1 usw.
  const getDesignIndex = (designName) => {
    const match = designName.match(/\d+/); // Extrahiert die Zahl aus "Design 1", "Design 2" etc.
    return match
      ? Math.max(0, Math.min(designs.length - 1, parseInt(match[0], 10) - 1))
      : 0;
  };

  const SelectedBackground = designs[getDesignIndex(form.design)];

  const handleSave = async () => {
    if (!acceptedPolicy) {
      message.error(t.submitError);
      return;
    }
    let contestEntryId = "";
    const isContestActive = contest && contest.status === "active";
    const isEligibleForContest = isContestActive && !anonym;

    if (isEligibleForContest) {
      contestEntryId = contest._id;
    }

    const unansweredQuestions = form.questions.filter((question) => {
      // Text‐Fragen nie als unbeantwortet zählen
      if (question.type === "text") {
        return false;
      }

      const resp = responses[question._id] || {};

      if (question.type === "multiple-choice") {
        return !Array.isArray(resp.answer) || resp.answer.length === 0;
      }

      if (question.type === "yesno") {
        // Choice ist Pflicht
        if (!resp.choice) {
          return true;
        }
        // Kommentar nur prüfen, wenn requireComment gesetzt ist
        if (
          question.requireComment &&
          (!resp.comment || !resp.comment.trim())
        ) {
          return true;
        }
        return false;
      }

      // alle anderen Typen (single-choice, star-rating etc.)
      return (
        resp.answer === undefined || resp.answer === null || resp.answer === ""
      );
    });

    if (unansweredQuestions.length > 0) {
      message.error(t.allQuestionsRequired);
      return;
    }

    if (!anonym) {
      if (
        !formData.firstName.trim() ||
        !formData.lastName.trim() ||
        !formData.email.trim()
      ) {
        message.error(t.fillOutNameEmail);
        return;
      }
    }

    const sender = anonym
      ? { firstname: "Anonym", email: "Anonym", lastname: "Anonym" }
      : {
          firstname: formData.firstName,
          email: formData.email,
          lastname: formData.lastName,
        };
    // Aufbereiten der Antworten
    const answersToSend = {};
    form.questions.forEach((q) => {
      const r = responses[q._id];
      if (!r) return;

      // Erzeuge immer ein Objekt mit question+answer
      answersToSend[q._id] = {
        question: q.questionText, // oder: r.question
        answer:
          q.type === "yesno"
            ? // kombiniere choice+comment zu einem String
              r.comment?.trim()
              ? `${r.choice}, ${r.comment.trim()}`
              : r.choice
            : r.answer, // das kann string, number oder array<string> sein
      };
    });

    const overallId = "679f639effa430a7f0756e34";
    if (responses[overallId]) {
      const r = responses[overallId];
      answersToSend[overallId] = {
        question: r.question,
        answer: r.answer,
      };
    }

    let uploadedImageUrls = [];
    if (captureImages.length > 0) {
      console.log("Uploading images to Cloudinary...");

      // Zeige Loading-Message
      message.loading("Bilder werden hochgeladen...", 0);

      const uploadPromises = captureImages.map(async (base64Image, index) => {
        try {
          console.log(`Uploading image ${index + 1}/${captureImages.length}`);
          const uploadedUrl = await uploadImageDirectly(base64Image);
          return uploadedUrl;
        } catch (error) {
          console.error(`Error uploading image ${index}:`, error);
          return null;
        }
      });

      const uploadResults = await Promise.all(uploadPromises);

      uploadedImageUrls = uploadResults.filter((url) => url !== null);

      message.destroy();

      if (uploadedImageUrls.length !== captureImages.length) {
        const failedCount = captureImages.length - uploadedImageUrls.length;
        message.warning(
          `${failedCount} Bild(er) konnten nicht hochgeladen werden.`
        );
      } else if (uploadedImageUrls.length > 0) {
        message.success(
          `${uploadedImageUrls.length} Bild(er) erfolgreich hochgeladen!`
        );
      }

      console.log("Uploaded image URLs:", uploadedImageUrls);
    }

    const payload = {
      formID: form._id,
      answers: answersToSend,
      sender: sender,
      images: uploadedImageUrls,
      isEligibleForContest: isEligibleForContest,
      contestEntryId: contestEntryId,
    };

    setDisableButton(true);

    try {
      await saveFeedback(payload);
      setResponses({});
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
      });
      setAcceptedPolicy(false);
      setAnonym(false);
      setDisableButton(true);
      setTriggerSuccess(true);
      console.log("Feedback erfolgreich gespeichert:", payload);
    } catch (error) {
      console.error("Fehler beim Speichern des Feedbacks:", error);
      setTriggerFailure(true);
    }
  };
  const items = [
    {
      key: "1",
      label: t.namedTab,
      children: (
        <Card title={t.dataTitle} className="w-full my-4 mx-auto">
          <form className="space-y-4">
            <Input
              placeholder={t.firstNamePlaceholder}
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
            />
            <Input
              placeholder={t.lastNamePlaceholder}
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
            />
            <Input
              type="email"
              placeholder={t.emailPlaceholder}
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
          </form>
        </Card>
      ),
    },
    {
      key: "2",
      label: t.anonymousTab,
      children: <h1 className="py-4">Ihr Feedback wird anonym mitgeteilt.</h1>,
    },
  ];
  const onChange = (key) => {
    key === "1" ? setAnonym(false) : setAnonym(true);
  };
  return (
    <SelectedBackground bgColor={form.designColor}>
      <AnimatePresence>
        {viewMode === "readonly" && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => navigate(-1)}
            className="fixed top-4 left-4 z-50 flex items-center space-x-2 text-gray-700 hover:text-orange-500 transition"
          >
            <span className="sr-only">Zurück</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>

      <div className="min-h-screen p-6 flex flex-col justify-center items-center">
        <AnimatePresence>
          {triggerSuccess && (
            <PositiveSubmit
              isOpen={triggerSuccess}
              onClose={() => {
                setTriggerSuccess(false);
              }}
              autoCloseDelay={7000}
              form={form}
              color={color}
            />
          )}
          {triggerFailure && (
            <NegativeSubmit
              isOpen={triggerFailure}
              onClose={() => setTriggerFailure(false)}
              autoCloseDelay={3000}
              form={form}
              color={color}
            />
          )}
        </AnimatePresence>

        <motion.div
          className="md:w-[60vw] mx-auto bg-white shadow-lg rounded-lg overflow-hidden"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0 }}
        >
          <div className="px-6 py-8 space-y-8">
            {/* Logo */}
            {form.logo && (
              <motion.div
                variants={itemVariants}
                className="flex justify-center mb-4"
              >
                <img
                  src={form.logo}
                  alt="Form Logo"
                  onLoad={(e) => {
                    const img = e.target;
                    setAspectClass(
                      img.naturalWidth / img.naturalHeight > 1.7
                        ? "aspect-[16/9]"
                        : "aspect-[4/3]"
                    );
                  }}
                  className={`${aspectClass} w-48 object-contain`}
                />
              </motion.div>
            )}

            {/* Title & Description */}
            <motion.h1
              variants={itemVariants}
              className="text-2xl font-bold text-gray-800"
            >
              {form.name}
            </motion.h1>
            <motion.p variants={itemVariants} className="text-gray-600">
              {form.description}
            </motion.p>

            {/* ✅ Giveaway Button (nur wenn Contest verfügbar und User hat Modal gesehen) */}
            {contest && hasSeenGiveaway && contest.status === "active" && (
              <div className="text-center mb-4">
                {/* Ohne motion.div */}
                <Button
                  type="primary"
                  size="large"
                  onClick={() => setShowGiveawayModal(true)}
                  style={{
                    backgroundColor: color || "#f97316",
                    borderColor: color || "#f97316",
                  }}
                  className="shadow-lg"
                  icon={<span className="text-lg">🎁</span>}
                >
                  Gewinnspiel Details ansehen
                </Button>
              </div>
            )}

            {/* Tabs */}
            <motion.div variants={itemVariants}>
              <Tabs defaultActiveKey="1" items={items} onChange={onChange} />
            </motion.div>

            {/* Questions */}
            <motion.form variants={itemVariants} className="space-y-6">
              {form.questions.map((q, i) => (
                <motion.div key={q._id} variants={itemVariants}>
                  {renderQuestion(q, i)}
                </motion.div>
              ))}

              {/* Photo Upload */}
              {form.enablePhotos && (
                <motion.div variants={itemVariants} className="mb-6">
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    {lang === "DE" ? "Fotos hinzufügen" : "Add Photos"}
                  </label>
                  <ImageCapture
                    onImageCapture={(images) => setCapturedImage(images)}
                  />
                </motion.div>
              )}

              {/* Overall Rating */}
              <motion.div variants={itemVariants} className="mb-6">
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  {t.overallRating}
                </label>
                <StarRating
                  stars={3}
                  onSelect={(val) =>
                    handleAnswerChange(
                      "679f639effa430a7f0756e34",
                      "Gesamtbewertung",
                      val
                    )
                  }
                />
              </motion.div>

              {/* Privacy Policy */}
              <motion.div
                variants={itemVariants}
                className="flex items-start text-sm text-gray-600"
              >
                <input
                  type="checkbox"
                  checked={acceptedPolicy}
                  onChange={(e) => setAcceptedPolicy(e.target.checked)}
                  className="mt-1 mr-2"
                />
                {t.acceptPolicy}{" "}
                <a
                  href="/privacypolicy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-500 underline ml-1"
                >
                  {t.privacyPolicy}
                </a>
              </motion.div>

              {/* ✅ Enhanced Submit Button */}
              <motion.button
                variants={itemVariants}
                type="button"
                onClick={handleSave}
                disabled={disableButton}
                style={{ backgroundColor: color || "rgb(234 88 12)" }}
                className="w-full text-white font-semibold py-3 rounded-lg shadow hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{t.submitTitle}</span>
                  {contest && !anonym && contest.status === "active" && (
                    <span className="text-lg">🎁</span>
                  )}
                </div>
                {contest && !anonym && contest.status === "active" && (
                  <div className="text-xs opacity-90 mt-1">
                    + Automatische Gewinnspiel-Teilnahme
                  </div>
                )}
              </motion.button>

              {/* ✅ Contest Success Info */}
              {contest && !anonym && contest.status === "active" && (
                <motion.div
                  variants={itemVariants}
                  className="text-center text-sm bg-green-50 p-3 rounded-lg border border-green-200"
                >
                  <div className="flex items-center justify-center gap-2 mb-1 text-green-800 font-medium">
                    <span>🍀</span>
                    <span>Sie nehmen am Gewinnspiel teil!</span>
                  </div>
                  <div className="text-green-700">
                    Gewinner werden nach dem{" "}
                    {new Date(contest.endDate).toLocaleDateString("de-DE")}{" "}
                    benachrichtigt.
                  </div>
                </motion.div>
              )}

              {/* ✅ Contest Warning für Anonymous */}
              {contest && anonym && contest.status === "active" && (
                <motion.div
                  variants={itemVariants}
                  className="text-center text-sm bg-orange-50 p-3 rounded-lg border border-orange-200"
                >
                  <div className="flex items-center justify-center gap-2 mb-1 text-orange-800 font-medium">
                    <span>⚠️</span>
                    <span>Keine Gewinnspiel-Teilnahme</span>
                  </div>
                  <div className="text-orange-700">
                    Anonyme Feedbacks können nicht am Gewinnspiel teilnehmen.
                  </div>
                </motion.div>
              )}
            </motion.form>
          </div>
        </motion.div>

        {/* ✅ Giveaway Modal */}
        <GiveawayModal
          contest={contest}
          visible={showGiveawayModal}
          onClose={() => setShowGiveawayModal(false)}
          color={color}
          form={form}
        />
      </div>
    </SelectedBackground>
  );
};

export default FeedbackFormPage;
